import { LIST_PLUGIN_ID } from './types';
import { Editor, Node, Path, Point, Range, Transforms, Text } from '../../core';
import type { ListVariant } from './constants';
import type { CurrentActiveListVariantState } from './hooks/useCurrentList';
import { LIST_VARIANTS } from './constants';
import { PARAGRAPH_LIST_ITEM_VARIANT, PARAGRAPH_PLUGIN_ID } from '../paragraph/types';
import {
  createListItemNode,
  createListItemNodeWithChildren,
  decreaseListIndentation,
  findClosestBlockPathAtSelection,
  getCurrentListAtPath,
  isInsideListAtPath,
  isInsideListAtSelection,
  isListItemEmpty,
  increaseListIndentation,
  triggerListInsertion,
} from './utils';
import { splitInlineBookmark } from '../inlineBookmark/utils';
import { logger } from 'modules/logger';
import { partialEditor } from '../../utils';
import type { ParagraphPluginElement } from '../paragraph/types';
import { removeBlocksInRange } from '../../utils/removeBlocksInRange';
import { getBlocksInRange } from '../../utils/getBlocksInRange';
import { Element, Location, NodeEntry } from 'slate';
import { CustomElement } from '../../slate-custom-types';
import { LINE_BREAK_PLUGIN_ID } from '../lineBreak/types';
import { INLINE_BOOKMARK_PLUGIN_ID } from '../inlineBookmark/types';
import { PICKLIST_PLUGIN_ID } from '../picklist/types';
import { SQUARE_BRACKET_TYPES } from '../../constants';
import { HEADING_PLUGIN_ID } from '../heading/types';

const getSingleParagraphBlockInRange = (
  editor: Editor,
  range: Range
): Nullable<NodeEntry<Node>> => {
  const [start, end] = Range.edges(range);
  const paragraphAtStart = Editor.above(editor, {
    at: start,
    match: (node) => Element.isElement(node) && node.type === PARAGRAPH_PLUGIN_ID,
    mode: 'lowest',
  });
  const paragraphAtEnd = Editor.above(editor, {
    at: end,
    match: (node) => Element.isElement(node) && node.type === PARAGRAPH_PLUGIN_ID,
    mode: 'lowest',
  });

  if (paragraphAtStart == null || paragraphAtEnd == null) {
    return null;
  }

  if (!Path.equals(paragraphAtStart[1], paragraphAtEnd[1])) {
    return null;
  }

  if (paragraphAtStart[0].variant === PARAGRAPH_LIST_ITEM_VARIANT) {
    return null;
  }

  // Inline paragraphs after headings are intentional caret targets — they must be
  // preserved in place. Let deleteSelectionAndGetInsertionPath handle this case.
  if (paragraphAtStart[0].shouldForceInline === true) {
    return null;
  }

  return paragraphAtStart;
};

const convertSingleParagraphSelectionToList = (
  editor: Editor,
  variant: ListVariant,
  selection: Range
): boolean => {
  const paragraphBlock = getSingleParagraphBlockInRange(editor, selection);

  if (paragraphBlock == null) {
    return false;
  }

  const insertionPath = paragraphBlock[1];
  const listItemNodes = createListItemNodesFromBlocks([paragraphBlock]);

  Transforms.removeNodes(editor, { at: insertionPath });
  triggerListInsertion({
    editor,
    variant,
    at: insertionPath,
    listItemNodes,
    edge: 'end',
  });

  return true;
};

/**
 *
 * This file houses the functions directly assigned to a UI button press, keyboard command
 * or dictation command.
 * Any other helper functions sit in the utils file.
 */

/**
 * Unsets one or more list items.
 * If no selection is provided, it will unset the list item at the current selection.
 *
 * The rules for unsetting are as follows:
 * - All selected items will be unset, including the children of the last item selected if any.
 * - The first selected item determines the level of indentation for the remaining items,
 *   which includes the children of the last item selected. (Note: this is not equivalent to
 *   the level of indentation of the first selected item, see next point)
 * - The indentation level is set to the previous node's indentation. For example:
 *   - If the first selected item is the first of its siblings, the indentation level
 *     will be set to the previous node's indentation level.
 *   - If the first selected item is NOT the first of its siblings (if any),
 *     the indentation level will remain the same.
 *
 * These rules are in place to ensure that it simultaneously obeys the list structure
 * and creates a logical format for the user.
 */
export const demoteListItems = (editor: Editor, path?: Range) => {
  const selection = path ?? editor?.selection;
  if (editor == null || selection == null) return;
  // Get all list items in the selection
  const listItemMatches = Array.from(
    Editor.nodes(editor, {
      at: selection,
      match: (n) =>
        Element.isElement(n) &&
        n.type === PARAGRAPH_PLUGIN_ID &&
        n.variant === PARAGRAPH_LIST_ITEM_VARIANT,
    })
  );
  if (listItemMatches.length === 0) return;

  /**
   * Let's get the targetPath to move the nodes to.
   */
  const firstItem = listItemMatches[0];
  // Capture the path of the previous node so we can move them to the same indentation level.
  const prevNode = Editor.previous(editor, {
    at: firstItem[1],
    match: (n) => Element.isElement(n) && n.type === PARAGRAPH_PLUGIN_ID,
  });
  /**
   * If the list is the very first node in the editor (i.e. very top of the editor),
   * and the item being demoted is the first item in the list, we need to move it to the top of the editor.
   */
  const topNode = Editor.node(editor, [0]);
  let targetPath = topNode[1];
  if (prevNode != null) {
    targetPath = [...prevNode[1]];
    targetPath[targetPath.length - 1] += 1;
  }

  /**
   * Get the children of the last item in the selection.
   * These will also need to be moved to the same indentation level
   * to avoid breaking the list structure
   */
  const lastItemChildren: Node[] = [];
  const lastItem = listItemMatches[listItemMatches.length - 1];
  const rootItem = lastItem[1].length > firstItem[1].length ? firstItem : lastItem;
  const parentOfRootItem = Editor.parent(editor, rootItem[1]);
  const allItemsAtRootItemLevel = Editor.nodes(editor, {
    at: parentOfRootItem[1],
    match: (n) =>
      Element.isElement(n) &&
      ([LIST_PLUGIN_ID, PARAGRAPH_PLUGIN_ID] as CustomElement['type'][]).includes(n.type),
  });
  let result;
  let hasChildren = false;
  while ((result = allItemsAtRootItemLevel.next()) && !result.done) {
    const item = result.value;
    // Advance to the node after the last selected item
    if (Path.isAfter(item[1], lastItem[1])) {
      // If the last selected item has children, they are book-ended by list nodes
      if (item[0].type === LIST_PLUGIN_ID && item[1].length === rootItem[1].length) {
        if (hasChildren) {
          break;
        }
        hasChildren = true;
        continue;
      }
      // Break if the subsequent node is a sibling list item
      if (
        item[0].variant === PARAGRAPH_LIST_ITEM_VARIANT &&
        item[1].length === rootItem[1].length
      ) {
        break;
      }
      lastItemChildren.push(item);
    }
  }

  // Reverse order to ensure that we handle last items first to maintain path order
  listItemMatches.reverse();
  lastItemChildren.reverse();

  const pathRefs = [...lastItemChildren, ...listItemMatches].map((entry) =>
    Editor.pathRef(editor, entry[1])
  );

  Editor.withoutNormalizing(editor, () => {
    for (const pathRef of pathRefs) {
      if (pathRef?.current != null) {
        Transforms.unsetNodes(editor, 'variant', { at: pathRef.current });
        // unsetting should not make pathRef.current null...
        if (pathRef?.current != null) {
          Transforms.moveNodes(editor, { at: pathRef.current, to: targetPath });
        }
      }
    }
  });
};

export const increaseIndent = (
  editor?: Editor | null,
  currentActiveListVariant?: CurrentActiveListVariantState | null
) => {
  if (editor != null && currentActiveListVariant != null) {
    const path = findClosestBlockPathAtSelection(editor);

    increaseListIndentation(editor, LIST_VARIANTS[currentActiveListVariant], path);
  }
};

export const decreaseIndent = (
  editor?: Editor | null,
  currentActiveListVariant?: CurrentActiveListVariantState | null
) => {
  if (editor != null && editor.selection != null && currentActiveListVariant != null) {
    const path = findClosestBlockPathAtSelection(editor);
    decreaseListIndentation(editor, LIST_VARIANTS[currentActiveListVariant], path);
  }
};
/**
 * Voice Command: InsertIndentedBullet / InsertIndentedNumbering
 *
 * If dealing with expanded selection, or if no selection, do nothing.
 *
 * If not inside a list, insert a new list with a single list item instead
 *
 * If inside empty list item, increase the indent instead
 *
 * If inside non-empty list item, split the list item based on selection, and then increase indent
 */
export const indentNextListItem = (editor: Editor, variant: ListVariant): void => {
  if (editor.selection != null && !Range.isCollapsed(editor.selection)) {
    return;
  }

  const path = findClosestBlockPathAtSelection(editor);

  // insert a list with a single list item instead, if not inside a list yet
  if (!isInsideListAtPath(editor, path)) {
    return insertList(editor, variant);
  }

  const node = Node.get(editor, path);

  // split the current list item onto a new line.
  if (!isListItemEmpty(node)) {
    splitInlineBookmark(editor);
  }

  increaseIndent(editor, variant);
};

/**
 *
 * Builds upon decreaseIndent, where it keeps decreasing indent until outside any lists, from any depth
 *
 */
export const exitAllLists = (
  editor: Editor,
  currentActiveListVariant?: CurrentActiveListVariantState | null
) => {
  if (editor != null && currentActiveListVariant != null) {
    while (
      isInsideListAtPath(editor, findClosestBlockPathAtSelection(editor)) ||
      isInsideListAtSelection(editor)
    ) {
      decreaseIndent(editor, currentActiveListVariant);
    }
  }
};

/**
 *
 * Each block in the selection, will become a list item in a new list of the given variant
 *
 */
export const insertList = (
  editor?: Editor | null,
  variant?: ListVariant | null,
  path?: Range
): void => {
  if (editor == null || variant == null) {
    return;
  }

  const selection = path ?? editor.selection;
  if (selection == null) {
    const selectionErrorMessage = '[list/functions] Null selection inside insertList';
    logger.error(selectionErrorMessage, {
      editor: partialEditor(editor),
      variant,
    });
    return;
  }

  Editor.withoutNormalizing(editor, () => {
    if (Range.isCollapsed(selection)) {
      const currentParentNode = Editor.above(editor, {
        at: selection,
        match: (n) => Node.isNode(n) && !Text.isText(n),
        mode: 'lowest',
      });

      let insertionPoint: Nullable<Range> = selection;

      if (
        path == null &&
        editor.children.length > 1 &&
        currentParentNode != null &&
        Node.string(currentParentNode[0]) === ''
      ) {
        Transforms.removeNodes(editor, { at: currentParentNode[1] });
        insertionPoint = editor.selection;
      }

      if (insertionPoint != null) {
        triggerListInsertion({
          editor,
          variant,
          at: insertionPoint,
          listItemNodes: [createListItemNode()],
          edge: 'start',
        });
      }
      return;
    }

    const blocks = getBlocksInRange(editor, selection);
    if (blocks.length > 1) {
      const listItemNodes = createListItemNodesFromBlocks(blocks);
      removeBlocksInRange(editor, selection);

      const insertionPath = blocks[0][1];
      triggerListInsertion({
        editor,
        variant,
        at: insertionPath,
        listItemNodes,
        edge: 'end',
      });
      return;
    }

    const textContent = Editor.string(editor, selection);
    const listItems = createListItemNode(textContent);
    const insertionPath = deleteSelectionAndGetInsertionPath(editor, selection, path);

    if (insertionPath != null) {
      triggerListInsertion({
        editor,
        variant,
        at: insertionPath,
        listItemNodes: [listItems],
        edge: 'end',
      });
    }
  });
};

/**
 * Converts selected content to a list by paragraph boundaries.
 * Unlike insertList, this may convert the entire current block when
 * selection is contained within a single block
 */
export const convertToList = (
  editor?: Editor | null,
  variant?: ListVariant | null,
  path?: Range
): void => {
  if (editor != null && variant != null) {
    const selection = path ?? editor.selection;
    let shouldCleanupTrailingEmptySquareBracket = false;

    if (selection == null) {
      const selectionErrorMessage = '[list/functions] Null selection inside convertToList';
      logger.error(selectionErrorMessage, {
        editor: partialEditor(editor),
        variant,
      });
      return;
    }

    Editor.withoutNormalizing(editor, () => {
      if (Range.isCollapsed(selection)) {
        if (convertSingleParagraphSelectionToList(editor, variant, selection)) {
          return;
        }

        const currentParentNode = Editor.above(editor, {
          at: selection,
          match: (n) => Node.isNode(n) && !Text.isText(n),
          mode: 'lowest',
        });

        let insertionPoint: Nullable<Range> = selection;

        if (
          path == null &&
          editor.children.length > 1 &&
          currentParentNode != null &&
          Node.string(currentParentNode[0]) === ''
        ) {
          Transforms.removeNodes(editor, { at: currentParentNode[1] });
          insertionPoint = editor?.selection;
        }

        if (insertionPoint != null) {
          triggerListInsertion({
            editor,
            variant,
            at: insertionPoint,
            listItemNodes: [createListItemNode()],
            edge: 'start',
          });
        }
      } else {
        const blocks = getBlocksInRange(editor, selection);
        if (blocks.length > 1) {
          const listItemNodes = createListItemNodesFromBlocks(blocks);
          removeBlocksInRange(editor, selection);

          // Insert the list using the path of the first block.
          // This means collapsed selections, single-block-expanded selections
          // and multi-block-expanded selections are all covered
          const insertionPath = blocks[0][1];
          triggerListInsertion({
            editor,
            variant,
            at: insertionPath,
            listItemNodes,
            edge: 'end',
          });
        } else if (selection != null) {
          const selectionForLineBreakConversion =
            getExpandedRangeToLineBoundariesInSquareBracket(editor, selection) ?? selection;
          const lineBreakFragment = Editor.fragment(editor, selectionForLineBreakConversion);
          const linesFromLineBreaks = getTextLinesFromFragment(lineBreakFragment);
          const hasLineBreakContent = linesFromLineBreaks.length > 1;

          if (hasLineBreakContent) {
            const shouldRemoveTrailingEmptySquareBracket =
              shouldRemoveTrailingEmptySquareBracketAfterListInsertion(
                editor,
                selectionForLineBreakConversion
              );
            const lineItems = linesFromLineBreaks.filter((line) => line !== '');
            const listItemNodes = (lineItems.length > 0 ? lineItems : ['']).map(createListItemNode);

            const insertionPath = deleteSelectionAndGetInsertionPath(
              editor,
              selectionForLineBreakConversion,
              path
            );
            if (insertionPath == null) {
              return;
            }

            triggerListInsertion({
              editor,
              variant,
              at: insertionPath,
              listItemNodes,
              edge: 'end',
            });

            shouldCleanupTrailingEmptySquareBracket = shouldRemoveTrailingEmptySquareBracket;
            return;
          }

          if (convertSingleParagraphSelectionToList(editor, variant, selection)) {
            return;
          }

          // Preserve structured inline nodes (e.g. picklists/inline bookmarks) for
          // single-block selections. For text-only selections we keep legacy behavior
          // so list items are still wrapped with an inline bookmark.
          const fragment = Editor.fragment(editor, selection);
          const [firstBlock] = fragment;
          const hasStructuredInlineChildren =
            firstBlock != null &&
            Element.isElement(firstBlock) &&
            hasListStructuredInlineChildren(firstBlock.children);
          const listItems =
            hasStructuredInlineChildren && firstBlock != null && Element.isElement(firstBlock)
              ? createListItemNodeWithChildren(firstBlock.children)
              : createListItemNode(Editor.string(editor, selection));

          const insertionPath = deleteSelectionAndGetInsertionPath(editor, selection, path);

          if (insertionPath != null) {
            triggerListInsertion({
              editor,
              variant,
              at: insertionPath,
              listItemNodes: [listItems],
              edge: 'end',
            });
          }
        }
      }
    });

    if (shouldCleanupTrailingEmptySquareBracket) {
      removeTrailingEmptySquareBracketAfterInsertedList(editor);
    }
  }
};

const getTextLinesFromFragment = (fragment: Array<Node>): Array<string> => {
  const lines = [''];

  const appendTextFromNode = (node: Node) => {
    if (Text.isText(node)) {
      lines[lines.length - 1] += node.text;
      return;
    }

    if (!Element.isElement(node) || !Array.isArray(node.children)) {
      return;
    }

    if (node.type === LINE_BREAK_PLUGIN_ID) {
      lines.push('');
      return;
    }

    node.children.forEach((child) => appendTextFromNode(child));
  };

  fragment.forEach((node) => appendTextFromNode(node));

  return lines;
};

const hasListStructuredInlineChildren = (children: Array<Node>): boolean => {
  const hasStructuredChildren = (node: Node): boolean => {
    if (Text.isText(node) || !Element.isElement(node)) {
      return false;
    }

    // inlineBookmark and lineBreak are "text-like" for list conversion:
    // they should keep legacy text wrapping behavior in single-block selections.
    if (node.type === INLINE_BOOKMARK_PLUGIN_ID || node.type === LINE_BREAK_PLUGIN_ID) {
      return node.children.some(hasStructuredChildren);
    }

    // Keep real structured inline nodes (e.g. picklist) as children so preview/cursor behavior
    // is preserved when converting to lists.
    if (node.type === PICKLIST_PLUGIN_ID) {
      return true;
    }

    return node.children.some(hasStructuredChildren);
  };

  return children.some(hasStructuredChildren);
};

const getExpandedRangeToLineBoundariesInSquareBracket = (
  editor: Editor,
  selection: Range
): Nullable<Range> => {
  const [start, end] = Range.edges(selection);
  const squareBracketAtStart = Editor.above(editor, {
    at: start,
    match: (node) =>
      Element.isElement(node) && SQUARE_BRACKET_TYPES.includes(node.type as CustomElement['type']),
    mode: 'lowest',
  });
  const squareBracketAtEnd = Editor.above(editor, {
    at: end,
    match: (node) =>
      Element.isElement(node) && SQUARE_BRACKET_TYPES.includes(node.type as CustomElement['type']),
    mode: 'lowest',
  });

  if (squareBracketAtStart == null || squareBracketAtEnd == null) {
    return null;
  }

  if (!Path.equals(squareBracketAtStart[1], squareBracketAtEnd[1])) {
    return null;
  }

  const squareBracketPath = squareBracketAtStart[1];
  const squareBracketNode = squareBracketAtStart[0] as CustomElement;
  const children = squareBracketNode.children ?? [];

  if (children.length === 0) {
    return null;
  }

  const startChildIdx = start.path[squareBracketPath.length];
  const endChildIdx = end.path[squareBracketPath.length];

  if (startChildIdx == null || endChildIdx == null) {
    return null;
  }

  const lineBreakIndexes = children
    .map((child, idx) =>
      Element.isElement(child) && child.type === LINE_BREAK_PLUGIN_ID ? idx : -1
    )
    .filter((idx) => idx >= 0);

  const previousLineBreakIdx = [...lineBreakIndexes].reverse().find((idx) => idx < startChildIdx);
  const nextLineBreakIdx = lineBreakIndexes.find((idx) => idx > endChildIdx);

  const expandedStartIdx = previousLineBreakIdx == null ? 0 : previousLineBreakIdx + 1;
  const expandedEndIdx = nextLineBreakIdx == null ? children.length - 1 : nextLineBreakIdx - 1;

  if (expandedStartIdx > expandedEndIdx) {
    return null;
  }

  const expandedStartPath = [...squareBracketPath, expandedStartIdx];
  const expandedEndPath = [...squareBracketPath, expandedEndIdx];

  return {
    anchor: Editor.start(editor, expandedStartPath),
    focus: Editor.end(editor, expandedEndPath),
  };
};

const deleteSelectionAndGetInsertionPath = (
  editor: Editor,
  selection: Range,
  path?: Range
): Nullable<Location> => {
  if (isWholeSquareBracketSelection(editor, selection)) {
    const currentBlock = Editor.above(editor, {
      at: selection,
      match: (n) => Element.isElement(n) && n.type === PARAGRAPH_PLUGIN_ID,
      mode: 'lowest',
    });

    if (currentBlock != null) {
      const previousBlock = Editor.previous(editor, { at: currentBlock[1] });
      const currentBlockNode = currentBlock[0];
      const isInlineParagraphAfterHeading =
        Element.isElement(currentBlockNode) &&
        currentBlockNode.type === PARAGRAPH_PLUGIN_ID &&
        (currentBlockNode as ParagraphPluginElement).shouldForceInline === true &&
        previousBlock != null &&
        Element.isElement(previousBlock[0]) &&
        previousBlock[0].type === HEADING_PLUGIN_ID;

      if (isInlineParagraphAfterHeading) {
        // Inline paragraphs immediately after headings are intentional caret targets.
        // Remove only the selected bracket node and insert the list after this paragraph,
        // instead of removing the paragraph itself.
        const squareBracketEntry = Editor.above(editor, {
          at: selection,
          match: (n) =>
            Element.isElement(n) && SQUARE_BRACKET_TYPES.includes(n.type as CustomElement['type']),
        });
        if (squareBracketEntry != null) {
          Transforms.removeNodes(editor, { at: squareBracketEntry[1] });
        } else {
          Transforms.delete(editor, { at: selection });
        }
        return Path.next(currentBlock[1]);
      }

      const insertionPath = currentBlock[1];
      Transforms.removeNodes(editor, { at: currentBlock[1] });
      return insertionPath;
    }
  }

  Transforms.delete(editor, { at: selection });
  let insertionPath: Nullable<Location> = Editor.start(editor, selection);

  const currentParentNode = Editor.above(editor, {
    at: selection,
    match: (n) => Node.isNode(n) && !Text.isText(n),
    mode: 'lowest',
  });

  if (
    path == null &&
    editor.children.length > 1 &&
    currentParentNode != null &&
    Node.string(currentParentNode[0]) === ''
  ) {
    Transforms.removeNodes(editor, { at: currentParentNode[1] });
    insertionPath = editor.selection;
  }

  return insertionPath;
};

const shouldRemoveTrailingEmptySquareBracketAfterListInsertion = (
  editor: Editor,
  selection: Range
): boolean => {
  const [start, end] = Range.edges(selection);

  const squareBracketAtStart = Editor.above(editor, {
    at: start,
    match: (node) =>
      Element.isElement(node) && SQUARE_BRACKET_TYPES.includes(node.type as CustomElement['type']),
  });
  const squareBracketAtEnd = Editor.above(editor, {
    at: end,
    match: (node) =>
      Element.isElement(node) && SQUARE_BRACKET_TYPES.includes(node.type as CustomElement['type']),
  });

  if (squareBracketAtStart == null || squareBracketAtEnd == null) {
    return false;
  }

  if (!Path.equals(squareBracketAtStart[1], squareBracketAtEnd[1])) {
    return false;
  }

  const squareBracketStart = Editor.start(editor, squareBracketAtStart[1]);
  const squareBracketEnd = Editor.end(editor, squareBracketAtStart[1]);
  const isWholeSquareBracketSelection =
    Point.equals(start, squareBracketStart) && Point.equals(end, squareBracketEnd);

  if (isWholeSquareBracketSelection) {
    return false;
  }

  return Point.equals(end, squareBracketEnd);
};

const removeTrailingEmptySquareBracketAfterInsertedList = (editor: Editor): void => {
  const selection = editor.selection;
  if (selection == null) {
    return;
  }

  const currentList = Editor.above(editor, {
    at: selection,
    match: (node) => Element.isElement(node) && node.type === LIST_PLUGIN_ID,
  });
  if (currentList == null) {
    return;
  }

  const nextPath = Path.next(currentList[1]);
  if (!Node.has(editor, nextPath)) {
    return;
  }

  const nextNode = Node.get(editor, nextPath);
  if (isParagraphWithOnlyEmptySquareBracket(nextNode)) {
    Transforms.removeNodes(editor, { at: nextPath });
  }
};

const isParagraphWithOnlyEmptySquareBracket = (node: Node): boolean => {
  if (!Element.isElement(node) || node.type !== PARAGRAPH_PLUGIN_ID) {
    return false;
  }

  const children = (node as CustomElement).children;
  if (children == null || children.length !== 3) {
    return false;
  }

  const [prefixText, squareBracketNode, suffixText] = children;
  return (
    Text.isText(prefixText) &&
    prefixText.text === '' &&
    Element.isElement(squareBracketNode) &&
    SQUARE_BRACKET_TYPES.includes(squareBracketNode.type as CustomElement['type']) &&
    Node.string(squareBracketNode) === '' &&
    Text.isText(suffixText) &&
    suffixText.text === ''
  );
};

const isWholeSquareBracketSelection = (editor: Editor, selection: Range): boolean => {
  const [start, end] = Range.edges(selection);

  const squareBracketAtStart = Editor.above(editor, {
    at: start,
    match: (node) =>
      Element.isElement(node) && SQUARE_BRACKET_TYPES.includes(node.type as CustomElement['type']),
  });
  const squareBracketAtEnd = Editor.above(editor, {
    at: end,
    match: (node) =>
      Element.isElement(node) && SQUARE_BRACKET_TYPES.includes(node.type as CustomElement['type']),
  });

  if (squareBracketAtStart == null || squareBracketAtEnd == null) {
    return false;
  }

  if (!Path.equals(squareBracketAtStart[1], squareBracketAtEnd[1])) {
    return false;
  }

  const squareBracketStart = Editor.start(editor, squareBracketAtStart[1]);
  const squareBracketEnd = Editor.end(editor, squareBracketAtStart[1]);

  return Point.equals(start, squareBracketStart) && Point.equals(end, squareBracketEnd);
};

/**
 *
 * Each block in the selection, will become a list item in a new list of the given variant
 *
 */
export const extendPreviousList = (
  editor: Nullable<Editor>,
  variant: Nullable<ListVariant>,
  range: Range,
  previousList: Path
) => {
  if (editor != null && variant != null) {
    const selection = range ?? editor.selection;

    if (selection == null) {
      const selectionErrorMessage = '[list/functions] Null selection inside extendPreviousList';
      logger.error(selectionErrorMessage, {
        editor: partialEditor(editor),
        variant,
      });
      return;
    }

    const blocks = getBlocksInRange(editor, selection);
    const listItemNodes = createListItemNodesFromBlocks(blocks);

    removeBlocksInRange(editor, selection);

    const previousNode = Editor.node(editor, previousList);
    if (previousNode == null) {
      return;
    }

    const listEntry =
      Element.isElement(previousNode[0]) && previousNode[0].type === LIST_PLUGIN_ID
        ? previousNode
        : getCurrentListAtPath(editor, previousList);
    const endInsertionPoint =
      listEntry != null ? [...listEntry[1], (listEntry[0] as CustomElement).children.length] : null;

    if (endInsertionPoint != null) {
      Transforms.insertNodes(editor, listItemNodes, {
        at: endInsertionPoint,
      });
    }
  }
};

export const createListItemNodesFromBlocks = (
  blocks: Array<NodeEntry<Node>>
): Array<ParagraphPluginElement> => {
  return blocks.map(([blockNode]) => {
    if (!Element.isElement(blockNode)) {
      return createListItemNode(Node.string(blockNode));
    }

    const children = Array.isArray(blockNode.children) ? blockNode.children : [];
    const isTextParagraph = children.every((child) => Text.isText(child));

    return isTextParagraph
      ? createListItemNode(Node.string(blockNode))
      : createListItemNodeWithChildren(children);
  });
};

export const createListItemNodesFromRange = (
  editor: Editor,
  range: Range
): Array<ParagraphPluginElement> => {
  const blocks = getBlocksInRange(editor, range);

  return createListItemNodesFromBlocks(blocks);
};

export const switchListVariant = (editor: Editor, range?: Range): void => {
  const selection = range ?? editor?.selection;
  if (!selection) return;

  // Find the nearest list node from the selection
  const listNode = Editor.above(editor, {
    at: range,
    match: (n) => Element.isElement(n) && n.type === LIST_PLUGIN_ID,
  });

  if (!listNode) return;

  const [node, nodePath] = listNode;

  const newVariant = node.variant === LIST_VARIANTS.ol ? LIST_VARIANTS.ul : LIST_VARIANTS.ol;

  Transforms.setNodes(editor, { variant: newVariant }, { at: nodePath });
};

/**
 * Voice Command: NextBullet / NextNumber
 *
 * If dealing with expanded selection, or if no selection, do nothing.
 *
 * If not inside a list, insert a new list with a single list item instead
 *
 * If inside list item, split list item based on selection,
 *
 * i.e empty list item creates new empty list item and
 * non-empty list item moves content after selection to next list item
 */
export const insertNextListItem = (editor: Editor, variant: ListVariant): void => {
  if (editor.selection != null && !Range.isCollapsed(editor.selection)) {
    return;
  }

  const path = findClosestBlockPathAtSelection(editor);
  if (isInsideListAtPath(editor, path)) {
    splitInlineBookmark(editor);
  } else {
    insertList(editor, variant);
  }
};
